<template>
  <div class="kpis">
    <StatisticsKpi label="Clients" :value="integer(k.totalClients)" hint="Tous clients confondus" />
    <StatisticsKpi label="Nouveaux clients" :value="integer(k.newClients)" hint="Créés sur la période" />
    <StatisticsKpi label="Clients actifs" :value="integer(k.activeClients)" hint="Avec du chiffre d'affaires facturé" />
    <StatisticsKpi label="CA moyen par client" :value="money(k.averageRevenue)" hint="HT facturé, clients actifs" />
    <StatisticsKpi label="CA médian par client" :value="money(k.medianRevenue)" hint="La moitié des clients fait moins" />
    <StatisticsKpi label="Poids du premier client" :value="percent(k.topClientShare)" :tone="k.topClientShare >= 40 ? 'warn' : ''" hint="Part du CA facturé" />
    <StatisticsKpi label="Poids des 5 premiers" :value="percent(k.top5Share)" hint="Dépendance aux gros clients" />
    <StatisticsKpi label="Clients récurrents" :value="percent(k.recurringRate)" hint="Au moins deux factures" />
  </div>
  <div class="charts">
    <StatisticsCard :span="7" title="Clients acquis" subtitle="Nouveaux clients par période et total cumulé" :empty="!data.acquisition.length">
      <StatisticsChart :labels="acquisitionLabels" :datasets="acquisitionDatasets" value-format="integer" />
    </StatisticsCard>
    <StatisticsCard :span="5" :title="`Répartition du CA ${metricLabel}`" subtitle="Part de chaque client dans le chiffre d'affaires" :empty="!ranking.items.length">
      <StatisticsChart type="doughnut" :labels="shareLabels" :datasets="shareDatasets" />
    </StatisticsCard>
    <StatisticsCard :span="7" :title="`Meilleurs clients (${metricLabel})`" subtitle="Les dix premiers par chiffre d'affaires HT sur la période" :empty="!ranking.items.length">
      <StatisticsChart :height="rankingHeight" horizontal :labels="ranking.items.map(item => item.name)" :datasets="[{label: metricLabel, data: ranking.items.map(item => item.amount)}]" />
    </StatisticsCard>
    <StatisticsCard :span="5" title="Type de client" :empty="!data.types.length">
      <StatisticsChart type="doughnut" value-format="integer" :labels="data.types.map(item => typeLabel(item.key))" :datasets="[{label: 'Clients', data: data.types.map(item => item.count)}]" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Avancement commercial" subtitle="Clients par statut" :empty="!data.statuses.length">
      <StatisticsChart horizontal value-format="integer" :legend="false" :labels="data.statuses.map(item => statusLabel(item.key))" :datasets="[{label: 'Clients', data: data.statuses.map(item => item.count)}]" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Clients ponctuels et récurrents" subtitle="Parmi les clients déjà facturés" :empty="!data.recurrence.oneShot && !data.recurrence.recurring">
      <StatisticsChart type="doughnut" value-format="integer" :labels="['Une seule facture', 'Plusieurs factures']" :datasets="[{label: 'Clients', data: [data.recurrence.oneShot, data.recurrence.recurring]}]" />
    </StatisticsCard>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import {StatisticsService} from "@/statistics/services/StatisticsService";
import {bucketLabel, integer, money, percent} from "@/statistics/utils/format";
import {OTHERS_COLOR, PALETTE} from "@/statistics/utils/chartTheme";
import {revenueLabel} from "@/statistics/utils/revenue";
import {CLIENT_TYPE_ENUM} from "@/utils";
import {CLIENT_STATUS_ENUM} from "@/models/clientStatusEnum";

/**
 * Clients : acquisition dans le temps, répartition du chiffre d'affaires et dépendance aux plus gros clients.
 */
export default {
  name: 'ClientsStats',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart},
  props: {
    data: {type: Object, required: true},
  },
  computed: {
    k() {
      return this.data.kpis;
    },
    metricLabel() {
      return revenueLabel(StatisticsService.data.metric).toLowerCase();
    },
    // Classement selon le chiffre d'affaires choisi (facturé ou encaissé)
    ranking() {
      return this.data.topClients[StatisticsService.data.metric];
    },
    rankingHeight() {
      return `${Math.max(180, this.ranking.items.length * 34 + 40)}px`;
    },
    acquisitionLabels() {
      return this.data.acquisition.map(row => bucketLabel(row.key));
    },
    acquisitionDatasets() {
      return [
        {label: 'Nouveaux clients', data: this.data.acquisition.map(row => row.newClients), color: PALETTE[0]},
        {label: 'Total cumulé', data: this.data.acquisition.map(row => row.cumulative), type: 'line', yAxisID: 'y1', color: PALETTE[2]},
      ];
    },
    // Les clients du classement puis « Autres »
    shareLabels() {
      return [...this.ranking.items.map(item => item.name), ...(this.ranking.others.amount > 0 ? [`Autres (${this.ranking.others.count})`] : [])];
    },
    shareDatasets() {
      const amounts = [...this.ranking.items.map(item => item.amount), ...(this.ranking.others.amount > 0 ? [this.ranking.others.amount] : [])];
      const colors = amounts.map((_, index) => (this.ranking.others.amount > 0 && index === amounts.length - 1 ? OTHERS_COLOR : PALETTE[index % PALETTE.length]));
      return [{label: this.metricLabel, data: amounts, colors}];
    },
  },
  methods: {
    money, integer, percent,
    typeLabel: key => CLIENT_TYPE_ENUM[key]?.label ?? key,
    statusLabel: key => CLIENT_STATUS_ENUM[key]?.label ?? key,
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/statistics/style/statistics.styl';
</style>
